import { TaskLibraryListType, TaskPlanItemType } from '../models/common';

const INJECTION_DURATION_MINUTES = 60;

/**
 * Compute total duration in minutes from task template items (planning page).
 * For vaccination rules: 1 hour hardcoded.
 * For normal tasks: use duration + durationUnit.
 */
export function computeTotalDurationFromTemplates(
  items: Array<{ duration?: string | null; durationUnit?: string | null; type?: number }>
): number {
  let total = 0;
  for (const item of items) {
    if (item.type === TaskLibraryListType.VaccinationRule) {
      total += INJECTION_DURATION_MINUTES;
    } else {
      const dur = parseFloat(item.duration || '0');
      if (item.durationUnit?.toLowerCase().includes('hour')) {
        total += dur * 60;
      } else {
        total += dur;
      }
    }
  }
  return total;
}

/**
 * Compute total duration in minutes from occurrence items (overview page).
 * For vaccination items: 1 hour hardcoded.
 * For normal items: use fromTaskLibrary duration + durationUnit.
 */
export function computeTotalDurationFromOccurrenceItems(
  items: Array<{ type?: number; fromTaskLibrary?: { duration?: string | null; durationUnit?: string | null } | null }>
): number {
  let total = 0;
  for (const item of items) {
    if (item.type === TaskPlanItemType.Vaccination) {
      total += INJECTION_DURATION_MINUTES;
    } else if (item.fromTaskLibrary) {
      const dur = parseFloat(item.fromTaskLibrary.duration || '0');
      if (item.fromTaskLibrary.durationUnit?.toLowerCase().includes('hour')) {
        total += dur * 60;
      } else {
        total += dur;
      }
    }
  }
  return total;
}

/**
 * Compute expected end time string (HH:mm) given start of working day and total duration in minutes.
 */
export function computeExpectedEndTime(
  startOfWorkingDay: string | null | undefined,
  totalDurationMinutes: number
): string | null {
  if (!startOfWorkingDay || totalDurationMinutes <= 0) return null;

  const parts = startOfWorkingDay.split(':');
  if (parts.length < 2) return null;

  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  if (isNaN(hours) || isNaN(minutes)) return null;

  const startMinutes = hours * 60 + minutes;
  const endMinutes = startMinutes + totalDurationMinutes;
  const endHours = Math.floor(endMinutes / 60) % 24;
  const endMins = Math.round(endMinutes % 60);

  return `${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}`;
}

/**
 * Compute per-item expected end times for task template items (planning page).
 * Returns an array of HH:mm strings (one per item), or null entries when startOfWorkingDay is missing.
 */
export function computePerItemEndTimesFromTemplates(
  startOfWorkingDay: string | null | undefined,
  items: Array<{ duration?: string | null; durationUnit?: string | null; type?: number }>
): (string | null)[] {
  if (!startOfWorkingDay || !items?.length) return items?.map(() => null) ?? [];

  let cumulative = 0;
  return items.map((item) => {
    if (item.type === TaskLibraryListType.VaccinationRule) {
      cumulative += INJECTION_DURATION_MINUTES;
    } else {
      const dur = parseFloat(item.duration || '0');
      if (item.durationUnit?.toLowerCase().includes('hour')) {
        cumulative += dur * 60;
      } else {
        cumulative += dur;
      }
    }
    return computeExpectedEndTime(startOfWorkingDay, cumulative);
  });
}

/**
 * Compute per-item expected end times for occurrence items (overview page).
 * Returns an array of HH:mm strings (one per item), or null entries when startOfWorkingDay is missing.
 */
export function computePerItemEndTimesFromOccurrenceItems(
  startOfWorkingDay: string | null | undefined,
  items: Array<{ type?: number; fromTaskLibrary?: { duration?: string | null; durationUnit?: string | null } | null }>
): (string | null)[] {
  if (!startOfWorkingDay || !items?.length) return items?.map(() => null) ?? [];

  let cumulative = 0;
  return items.map((item) => {
    if (item.type === TaskPlanItemType.Vaccination) {
      cumulative += INJECTION_DURATION_MINUTES;
    } else if (item.fromTaskLibrary) {
      const dur = parseFloat(item.fromTaskLibrary.duration || '0');
      if (item.fromTaskLibrary.durationUnit?.toLowerCase().includes('hour')) {
        cumulative += dur * 60;
      } else {
        cumulative += dur;
      }
    }
    return computeExpectedEndTime(startOfWorkingDay, cumulative);
  });
}

/**
 * Format duration in minutes to a human-readable string.
 */
export function formatDuration(totalMinutes: number): string {
  if (totalMinutes <= 0) return '';
  const hours = Math.floor(totalMinutes / 60);
  const mins = Math.round(totalMinutes % 60);
  if (hours > 0 && mins > 0) return `${hours}h ${mins}m`;
  if (hours > 0) return `${hours}h`;
  return `${mins}m`;
}
